/**
 * RequestsPanel — the design's serving-load section: a hairline strip of
 * five serving stats with inline sparklines, then the request matrix —
 * one column of stacked unit cells per bucket, steady load in gunmetal with
 * bursts tinted on top. A window toggle switches Today / 7-day / 30-day
 * shapes; hover or focus a column for a readout that floats over that
 * column. At rest the peak column stays emphasized, so the busiest bucket
 * is findable without pointing at anything.
 *
 * Fed from the usage timeseries via `contracts.ts`. The burst series is a
 * dimension the gateway does not record yet, so a window whose buckets are
 * all-steady renders as a single series (no burst legend, no burst row).
 *
 * `children` render under the matrix: a second chart over the same buckets
 * (the model page's tokens per second) shares the window toggle and this
 * panel's loading and empty states instead of growing its own.
 */

import { useState } from 'react'

import { PillToggle } from '@/components/models/home/PillToggle'
import { anchorStyle, useCursorAnchor } from '@/components/models/home/cursorAnchor'
import { Sparkline } from '@/components/models/home/Sparkline'
import type {
  RequestBucket,
  RequestWindow,
  RequestWindowKey,
  ServingStat,
} from '@/components/models/home/contracts'
import { formatRate } from '@/components/models/home/contracts'
import { Skeleton } from '@/components/ui/skeleton'

/** Rows of unit cells in the matrix — the design's fixed column height. */
const REQ_ROWS = 18

/**
 * The five serving tiles. Switching window refetches, and a tile that keeps
 * its old number while the new window loads is actively misleading — it
 * reads as "30 days looks identical to today". Skeletons instead.
 */
function StatStrip({
  stats,
  isLoading,
}: {
  stats: ServingStat[]
  isLoading: boolean
}): React.JSX.Element {
  if (isLoading || stats.length === 0) {
    return (
      <div className="grid grid-cols-2 border-y border-border py-3 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, index) => (
          <div
            key={index}
            className={`flex items-center gap-3 px-4 py-1 ${
              index > 0 ? 'lg:border-l lg:border-border' : 'lg:pl-0'
            }`}
          >
            <div className="min-w-0 flex-1 space-y-1.5">
              <Skeleton className="h-2 w-16" />
              <Skeleton className="h-3.5 w-20" />
            </div>
            <Skeleton className="ml-auto h-4 w-11 shrink-0" />
          </div>
        ))}
      </div>
    )
  }
  return (
    <div className="grid grid-cols-2 border-y border-border py-3 sm:grid-cols-3 lg:grid-cols-5">
      {stats.map((stat, index) => (
        <div
          key={stat.label}
          className={`flex items-center gap-3 px-4 py-1 ${
            index > 0 ? 'lg:border-l lg:border-border' : 'lg:pl-0'
          }`}
        >
          <div className="min-w-0">
            <p
              className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground"
              title={stat.hint ?? undefined}
            >
              {stat.label}
              {stat.hint !== null && (
                <>
                  {/* The asterisk is the visual cue; the caveat itself has to
                      reach a screen reader too, since `title` alone is not
                      reliably announced. */}
                  <span aria-hidden="true"> *</span>
                  <span className="sr-only">: {stat.hint}</span>
                </>
              )}
            </p>
            <p className="mt-0.5 flex items-baseline gap-1.5">
              <span className="font-mono text-sm tabular-nums">{stat.value}</span>
              <span className="truncate text-[11px] text-muted-foreground">
                {stat.delta}
              </span>
            </p>
          </div>
          <div className="ml-auto shrink-0">
            <Sparkline values={stat.spark} />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Requests/min a single cell stands for — sized so the tallest column
 *  fills the fixed 18-row height whatever the traffic scale. Fractional:
 *  a team doing 6 rpm should fill the chart the same as one doing 1,800,
 *  and rounding the unit up to 1 would leave the first at a third height. */
function cellUnit(window: RequestWindow): number {
  const peak = Math.max(...window.buckets.map((b) => b.steady + b.burst), 1)
  return peak / REQ_ROWS
}

const AXIS_ROWS = [0, 6, 12, 18]

/**
 * Axis tick labels, in whatever scale the traffic actually is. Ticks sit at
 * thirds of the peak, so at low volume rounding collapses them into
 * duplicates ("0, 1, 1, 2") — add a decimal place until they read as
 * distinct values.
 */
function axisTicks(unit: number): { row: number; label: string }[] {
  const values = AXIS_ROWS.map((row) => row * unit)
  const format = (value: number, digits: number): string => {
    if (value === 0) return '0'
    if (value >= 1000) return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}k`
    return value.toFixed(digits)
  }
  const zip = (labels: string[]): { row: number; label: string }[] =>
    AXIS_ROWS.map((row, i) => ({ row, label: labels[i] ?? '' }))
  for (const digits of [0, 1, 2]) {
    const labels = values.map((value) => format(value, digits))
    if (new Set(labels).size === labels.length) return zip(labels)
  }
  return zip(values.map((value) => format(value, 2)))
}

/** One line of the hover readout: mono-uppercase term, tabular value. */
export function ReadoutRow({
  label,
  value,
  note,
  muted = false,
  divider = false,
}: {
  label: string
  value: string
  note?: string
  muted?: boolean
  divider?: boolean
}): React.JSX.Element {
  return (
    <div
      className={`flex items-baseline justify-between gap-6 ${
        divider ? 'mt-1.5 border-t border-border pt-1.5' : ''
      }`}
    >
      <dt
        className={`font-mono text-[10px] uppercase tracking-wider ${
          muted ? 'pl-2.5 text-muted-foreground/70' : 'text-muted-foreground'
        }`}
      >
        {label}
      </dt>
      <dd className="m-0 font-mono tabular-nums">
        {value}
        {note && <span className="ml-1.5 text-muted-foreground">({note})</span>}
      </dd>
    </div>
  )
}

/**
 * The bucket labels under a chart whose columns start after the matrix's
 * 48px axis gutter. Every `labelEvery`-th bucket is named so eight or so
 * labels fit whatever the window's bucket count.
 */
export function BucketAxisLabels({
  labels,
  active,
}: {
  labels: string[]
  /** The bucket to print in ink; none when the chart has no resting focus. */
  active?: number
}): React.JSX.Element {
  const labelEvery = Math.max(1, Math.round(labels.length / 8))
  return (
    <div className="ml-12 mt-2.5 flex gap-1">
      {labels.map((label, index) => (
        <span
          key={label + index.toString()}
          className={`min-w-0 flex-1 text-center font-mono text-[9px] uppercase tracking-wide ${
            index === active ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          {index % labelEvery === 0 ? label : ''}
        </span>
      ))}
    </div>
  )
}

/** Readout size, needed up front so it can flip away from the edges. */
const READOUT_WIDTH = 224
const READOUT_HEIGHT = 168

/** Everything the hover readout shows, as one sentence for a screen reader. */
function bucketReadout(bucket: RequestBucket): string {
  const requests = `${bucket.requests.toLocaleString()} ${
    bucket.requests === 1 ? 'request' : 'requests'
  }`
  return [
    `${bucket.detail}: ${requests}`,
    `${formatRate(bucket.steady)} per minute`,
    `${bucket.promptTokens.toLocaleString()} prompt tokens`,
    `${bucket.cachedPromptTokens.toLocaleString()} cached`,
    `${bucket.completionTokens.toLocaleString()} completion`,
    `${bucket.totalTokens.toLocaleString()} total`,
  ].join(', ')
}

function RequestsMatrix({ window }: { window: RequestWindow }): React.JSX.Element {
  const { anchor, track, clear } = useCursorAnchor()
  const [hovered, setHovered] = useState<number | null>(null)
  const buckets = window.buckets
  const unit = cellUnit(window)
  const hasBurst = buckets.some((bucket) => bucket.burst > 0)
  const peak = buckets.reduce(
    (best, bucket, index) =>
      bucket.steady + bucket.burst >
      (buckets[best]?.steady ?? 0) + (buckets[best]?.burst ?? 0)
        ? index
        : best,
    0,
  )
  const active = hovered ?? peak
  const activeBucket = buckets[active]

  return (
    <div className="w-full">
      <div
        className="relative"
        onMouseMove={track}
        onMouseLeave={() => {
          clear()
          setHovered(null)
        }}
      >
        {activeBucket && hovered !== null && anchor && (
          <div
            className="pointer-events-none absolute z-20 border border-border bg-card p-3"
            style={anchorStyle(anchor, READOUT_WIDTH, READOUT_HEIGHT)}
          >
          <p className="flex items-baseline gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            <span className="text-foreground">{activeBucket.detail}</span>
            <span>
              {activeBucket.requests.toLocaleString()}{' '}
              {activeBucket.requests === 1 ? 'request' : 'requests'}
            </span>
          </p>
          <dl className="mt-2.5 space-y-1 text-xs">
            <ReadoutRow
              label={hasBurst ? 'Steady / min' : 'Requests / min'}
              value={formatRate(activeBucket.steady)}
            />
            {hasBurst && (
              <ReadoutRow
                label="Burst / min"
                value={formatRate(activeBucket.burst)}
              />
            )}
            <ReadoutRow
              label="Prompt"
              value={activeBucket.promptTokens.toLocaleString()}
            />
            <ReadoutRow
              label="Cached"
              value={activeBucket.cachedPromptTokens.toLocaleString()}
              note={
                activeBucket.promptTokens > 0
                  ? `${Math.round(
                      (activeBucket.cachedPromptTokens / activeBucket.promptTokens) * 100,
                    ).toString()}%`
                  : undefined
              }
              muted
            />
            <ReadoutRow
              label="Completion"
              value={activeBucket.completionTokens.toLocaleString()}
            />
            <ReadoutRow
              label="Total tokens"
              value={activeBucket.totalTokens.toLocaleString()}
              divider
            />
          </dl>
          </div>
        )}
        <div className="flex items-start gap-3">
          <div className="relative h-[213px] w-9 flex-none">
            {axisTicks(unit).map((tick) => (
              <span
                key={tick.row}
                className="absolute right-0 text-[10px] text-muted-foreground"
                style={{ bottom: `${(tick.row * 12 - 5).toString()}px` }}
              >
                {tick.label}
              </span>
            ))}
          </div>
          <div className="flex flex-1 items-end gap-1">
            {buckets.map((bucket, index) => {
              const steadyCells = Math.max(
                bucket.steady > 0 ? 1 : 0,
                Math.round(bucket.steady / unit),
              )
              const burstCells = Math.min(
                REQ_ROWS - steadyCells,
                Math.round(bucket.burst / unit),
              )
              return (
                <button
                  key={bucket.detail + index.toString()}
                  type="button"
                  // The visual readout is positioned from the CURSOR, so it
                  // never appears for someone arriving by keyboard. Rather
                  // than fake a pointer position, the whole readout — not
                  // just the rate — rides in the accessible name, which is
                  // what a screen reader announces on focus.
                  aria-label={bucketReadout(bucket)}
                  onMouseEnter={() => {
                    setHovered(index)
                  }}
                  onFocus={() => {
                    setHovered(index)
                  }}
                  className="flex min-w-0 flex-1 flex-col-reverse gap-[3px] outline-offset-2"
                >
                  {Array.from({ length: REQ_ROWS }, (_, row) => {
                    const filled =
                      row < steadyCells
                        ? 'bg-brand-gunmetal-blue'
                        : row < steadyCells + burstCells
                          ? 'bg-brand-gunmetal-blue/40'
                          : 'bg-border/60'
                    const emphasis =
                      index === active
                        ? 'opacity-100'
                        : row < steadyCells + burstCells
                          ? 'opacity-80'
                          : 'opacity-40'
                    return (
                      <span
                        key={row}
                        className={`h-[9px] w-full rounded-[1.5px] ${filled} ${emphasis}`}
                      />
                    )
                  })}
                </button>
              )
            })}
          </div>
        </div>
        <BucketAxisLabels labels={buckets.map((bucket) => bucket.label)} active={active} />
      </div>
    </div>
  )
}

interface RequestsPanelProps {
  stats: ServingStat[]
  /** The window currently loaded. Controlled by the page, which fetches it. */
  data: RequestWindow
  window: RequestWindowKey
  onWindowChange: (window: RequestWindowKey) => void
  isLoading?: boolean
  /**
   * Zero traffic draws the (all-grey) matrix instead of the dashed
   * "no requests" notice. Set once the caller has already checked the
   * wider windows and knows silence is the whole story — the notice
   * implies a narrower window might have more, and here none does.
   */
  emptyGrid?: boolean
  /** Rendered under the matrix, and only when the matrix is. */
  children?: React.ReactNode
}

const WINDOW_OPTIONS: { value: RequestWindowKey; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: '7-day' },
  { value: 'month', label: '30-day' },
]

export function RequestsPanel({
  stats,
  data,
  window,
  onWindowChange,
  isLoading = false,
  emptyGrid = false,
  children,
}: RequestsPanelProps): React.JSX.Element {
  const current = data
  // Count requests, not rounded rates — a tenant doing four requests a day
  // has a rate that rounds to zero but is emphatically not "no traffic".
  const served = current.buckets.reduce((total, bucket) => total + bucket.requests, 0)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        {isLoading ? (
          <Skeleton className="h-4 w-32" />
        ) : (
          <span className="text-sm text-muted-foreground">
            {current.total} requests served
          </span>
        )}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <PillToggle
            aria-label="Requests window"
            value={window}
            onChange={onWindowChange}
            options={WINDOW_OPTIONS}
          />
        </div>
      </div>
      <StatStrip stats={stats} isLoading={isLoading} />
      {isLoading ? (
        <div className="h-[240px] animate-pulse bg-muted/40" />
      ) : served === 0 && !emptyGrid ? (
        <p className="border border-dashed border-border p-6 text-sm text-muted-foreground">
          No requests in this window. Traffic through the gateway appears here
          within a minute of the first call.
        </p>
      ) : (
        <>
          <RequestsMatrix window={current} />
          {children}
        </>
      )}
    </div>
  )
}
